import { supabase } from '../lib/supabase';

const companyRegistrationService = {
  async getSubscriptionPlans() {
    const { data, error } = await supabase?.from('subscription_plans')?.select('*')?.eq('is_active', true)?.eq('is_public', true)?.order('monthly_price', { ascending: true });

    if (error) {
      console.error('Error fetching subscription plans:', error?.message);
      throw error;
    }
    return data || [];
  },

  async checkRfcExists(rfc) {
    const { data, error } = await supabase?.from('companies')?.select('id')?.eq('rfc', rfc?.toUpperCase())?.maybeSingle();

    if (error) {
      console.error('Error checking RFC:', error?.message);
      return false;
    }
    return !!data;
  },

  async checkEmailExists(email) {
    const { data, error } = await supabase?.from('user_profiles')?.select('id')?.eq('email', email?.toLowerCase())?.maybeSingle();

    if (error) {
      console.error('Error checking email:', error?.message);
      return false;
    }
    return !!data;
  },

  async registerCompany({ companyData, planId, billingCycle, adminData }) {
    // Step 1: Create auth user for admin
    const { data: authData, error: authError } = await supabase?.auth?.signUp({
      email: adminData?.email,
      password: adminData?.password,
      options: {
        data: {
          full_name: adminData?.fullName,
          role: 'admin'
        }
      }
    });

    if (authError) {
      throw new Error(authError.message);
    }

    const userId = authData?.user?.id;
    if (!userId) {
      throw new Error('No se pudo crear el usuario administrador');
    }

    // Step 2: Create company record
    const { data: company, error: companyError } = await supabase?.from('companies')?.insert({
        company_name: companyData?.companyName,
        legal_name: companyData?.legalName || companyData?.companyName,
        rfc: companyData?.rfc?.toUpperCase(),
        email: adminData?.email,
        phone: companyData?.phone,
        address: companyData?.address,
        city: companyData?.city,
        state: companyData?.state,
        postal_code: companyData?.postalCode,
        country: 'México',
        is_active: true,
        onboarding_completed: false,
        settings: {}
      })?.select()?.single();

    if (companyError) {
      throw new Error('Error al crear la empresa: ' + companyError.message);
    }

    const companyId = company?.id;

    // Step 3: Update user_profile with company_id and role
    const { error: profileError } = await supabase?.from('user_profiles')?.update({
        company_id: companyId,
        full_name: adminData?.fullName,
        role: 'admin',
        phone: adminData?.phone || null
      })?.eq('id', userId);

    if (profileError) {
      console.error('Error updating user profile:', profileError?.message);
    }

    // Step 4: Create subscription
    const now = new Date();
    const periodEnd = new Date(now);
    if (billingCycle === 'monthly') {
      periodEnd?.setMonth(periodEnd?.getMonth() + 1);
    } else if (billingCycle === 'quarterly') {
      periodEnd?.setMonth(periodEnd?.getMonth() + 3);
    } else if (billingCycle === 'annual') {
      periodEnd?.setFullYear(periodEnd?.getFullYear() + 1);
    }

    const trialEnd = new Date(now);
    trialEnd?.setDate(trialEnd?.getDate() + 14);

    const { data: subscriptionRecord, error: subscriptionError } = await supabase?.from('company_subscriptions')?.insert({
        company_id: companyId,
        plan_id: planId,
        status: 'trial',
        billing_cycle: billingCycle,
        trial_ends_at: trialEnd?.toISOString(),
        current_period_start: now?.toISOString(),
        current_period_end: periodEnd?.toISOString()
      })?.select()?.single();

    if (subscriptionError) {
      console.error('Error creating subscription:', subscriptionError?.message);
    }

    // Step 5: Create main branch
    const { error: branchError } = await supabase?.from('branches')?.insert({
        company_id: companyId,
        branch_name: companyData?.companyName + ' - Principal',
        branch_code: 'MAIN',
        address: companyData?.address,
        city: companyData?.city,
        state: companyData?.state,
        postal_code: companyData?.postalCode,
        is_active: true,
        is_main_branch: true
      });

    if (branchError) {
      console.error('Error creating branch:', branchError?.message);
    }

    return { companyId, userId, subscriptionId: subscriptionRecord?.id };
  },

  /**
   * Get plan details by ID
   */
  async getPlanById(planId) {
    const { data, error } = await supabase?.from('subscription_plans')?.select('*')?.eq('id', planId)?.single();

    if (error) return null;
    return data;
  },

  /**
   * Check if a plan requires payment (non-free plans)
   */
  isPaidPlan(plan) {
    return plan?.plan_tier !== 'free' && (plan?.monthly_price ?? 0) > 0;
  },
};

export default companyRegistrationService;
